import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../lib/api-client.js';

interface Customer {
  id: string;
  name: string;
  license_tier: string;
  license_seats: number;
  license_used: number;
  timezone: string;
  created_at: string;
}

export function CustomerPage(): JSX.Element {
  const { data, isLoading, error } = useQuery({
    queryKey: ['customer'],
    queryFn: () => api.get('/v1/customer') as Promise<Customer>,
    retry: (failureCount, err) => {
      // Don't retry auth failures — re-login fixes them, not retries.
      if (err instanceof ApiError && (err.status === 401 || err.status === 403)) return false;
      return failureCount < 2;
    },
  });

  if (isLoading) return <p className="text-sm text-ink-muted">Loading…</p>;
  if (error) {
    const status = error instanceof ApiError ? error.status : 0;
    return (
      <div className="rounded border border-danger bg-surface-alt p-4">
        <p className="font-medium text-danger">Failed to load customer</p>
        <p className="text-sm text-ink-muted mt-1">
          {status ? `HTTP ${status}` : 'Network error'} — {(error as Error).message}
        </p>
        {status === 401 && (
          <p className="text-sm text-ink-muted mt-2">
            Token may be expired. Sign out and paste a fresh one.
          </p>
        )}
      </div>
    );
  }
  if (!data) return <p>No data.</p>;

  return (
    <div className="space-y-6">
      <header className="flex items-baseline justify-between">
        <div>
          <h1 className="text-2xl font-semibold">{data.name}</h1>
          <p className="text-sm text-ink-muted font-mono">{data.id}</p>
        </div>
        <Link
          to="/customer/config"
          className="text-sm text-accent hover:underline"
        >
          Edit dialplan / routing config →
        </Link>
      </header>
      <section className="rounded border border-border bg-surface-alt p-4">
        <dl className="grid grid-cols-2 gap-4 text-sm">
          <Field label="License tier" value={data.license_tier} />
          <Field label="Timezone" value={data.timezone} />
          <Field
            label="Seats used"
            value={`${data.license_used} / ${data.license_seats || '—'}`}
          />
          <Field
            label="Created"
            value={new Date(data.created_at).toLocaleDateString()}
          />
        </dl>
      </section>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div>
      <dt className="text-ink-muted">{label}</dt>
      <dd className="font-medium">{value}</dd>
    </div>
  );
}
